import { getJWTToken } from '../../../utils/auth';
import { authHeadersFor } from '../../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type MatchingState = 'not_confirmed' | 'confirmed' | 'changed' | 'no_feed';

export type FieldState = 'none' | 'nocol' | 'empty' | 'toomany' | 'open' | 'ok';

export type FieldTier = 'core' | 'helpful' | 'optional';

export type FieldKind = 'text' | 'date' | 'value' | 'group';

export type SisFeed = { boundAt: string; syncedAt?: string; students: number; columns: number };

export type SisListRow = {
  districtId: string;
  districtName: string;
  state?: string;
  students: number;
  feed: SisFeed | null;
  matching: MatchingState;
  answered: number;
  fieldsTotal: number;
  notInSis: number;
  openValues: number;
  openFields: string[];
  newValues: number;
  newFields: string[];
  confirmedBy?: string;
  confirmedByName?: string;
  confirmedAt?: string;
  summaryAsOf?: string;
};

export type SisMeaning = { key: string; label: string; hint?: string };

export type SisColumn = {
  name: string;
  filled: number;
  distinct: number;
  blank: number;
  dates: number;
  ints: number;
  identity?: boolean;
  listed: boolean;
  samples?: string[];
};

export type SisRanked = { column: string; rank: number };

export type SisValue = { value: string; key: string; students: number; guess?: string; meaning?: string; new?: boolean };

export type SisChoice = { column?: string; notInSis: boolean; by?: string; byName?: string; at?: string };

export type SisField = {
  key: string;
  label: string;
  tier: FieldTier;
  kind: FieldKind;
  aliases: string[];
  hints: string[];
  meanings?: SisMeaning[];
  choice: SisChoice;
  state: FieldState;
  answered: boolean;
  open: number;
  new: number;
  total: number;
  ranked: SisRanked[];
  values?: SisValue[];
  taken?: Record<string, string>;
};

export type SisCounts = {
  state: MatchingState;
  answered: number;
  notInSis: number;
  openValues: number;
  openFields: string[];
  newValues: number;
  newFields: string[];
  complete: boolean;
  firstOpenField: string;
};

export type SisConfirmation = { by: string; byName?: string; at: string; revision: number };

export type SisDistrict = {
  districtId: string;
  districtName: string;
  state?: string;
  feed: SisFeed;
  summaryAsOf?: string;
  columns: SisColumn[];
  revision: number;
  saved: boolean;
  matching: MatchingState;
  counts: SisCounts;
  confirmation: SisConfirmation | null;
  firstOpenField: string;
  schools: string[];
  fields: SisField[];
};

export type FieldChoiceBody = { column?: string; notInSis?: boolean };

export class NoFeedError extends Error {
  constructor() {
    super('This district has no RosterStream feed');
    this.name = 'NoFeedError';
  }
}

export class MatchingChangedError extends Error {
  readonly code: string;

  readonly current: SisDistrict | null;

  constructor(code: string, current: SisDistrict | null) {
    super('The matching changed since it was loaded');
    this.name = 'MatchingChangedError';
    this.code = code;
    this.current = current;
  }
}

export class ColumnTakenError extends Error {
  readonly fieldLabel: string;

  constructor(fieldLabel: string) {
    super(`The column is already used by ${fieldLabel}`);
    this.name = 'ColumnTakenError';
    this.fieldLabel = fieldLabel;
  }
}

async function staffHeaders(): Promise<Record<string, string>> {
  return authHeadersFor(await getJWTToken(), null);
}

async function read(path: string): Promise<Response> {
  return fetch(`${apiUrl}${path}`, { headers: await staffHeaders() });
}

async function write(method: 'PUT' | 'POST', path: string, body: unknown): Promise<SisDistrict> {
  const response = await fetch(`${apiUrl}${path}`, {
    method,
    headers: { ...(await staffHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.status === 409) {
    const payload = (await response.json()) as { error?: string; district?: SisDistrict } | null;
    throw new MatchingChangedError(payload?.error ?? 'revision_changed', payload?.district ?? null);
  }
  if (response.status === 404) throw new NoFeedError();
  if (response.status === 400) {
    const payload = (await response.json().catch(() => null)) as { error?: string; fieldLabel?: string } | null;
    if (payload?.error === 'column_taken') throw new ColumnTakenError(payload.fieldLabel ?? 'another field');
  }
  if (!response.ok) throw new Error(`${method} ${path} failed: ${response.status}`);
  return (await response.json()) as SisDistrict;
}

export async function fetchSisMatchingList(): Promise<SisListRow[]> {
  const response = await read('/admin/sis-matching');
  if (!response.ok) throw new Error(`GET /admin/sis-matching failed: ${response.status}`);
  const body = (await response.json()) as { districts?: SisListRow[] } | null;
  return body?.districts ?? [];
}

export async function fetchSisMatchingDistrict(districtId: string): Promise<SisDistrict> {
  const path = `/admin/sis-matching/${encodeURIComponent(districtId)}`;
  const response = await read(path);
  if (response.status === 404) throw new NoFeedError();
  if (!response.ok) throw new Error(`GET ${path} failed: ${response.status}`);
  return (await response.json()) as SisDistrict;
}

export async function putSisField(districtId: string, field: string, choice: FieldChoiceBody, revision: number): Promise<SisDistrict> {
  return write('PUT', `/admin/sis-matching/${encodeURIComponent(districtId)}/fields/${encodeURIComponent(field)}`, {
    column: choice.column ?? '',
    notInSis: choice.notInSis === true,
    revision,
  });
}

export async function putSisValue(
  districtId: string,
  field: string,
  column: string,
  value: string,
  meaning: string | null,
  revision: number,
): Promise<SisDistrict> {
  return write('PUT', `/admin/sis-matching/${encodeURIComponent(districtId)}/fields/${encodeURIComponent(field)}/values`, {
    column,
    value,
    meaning,
    revision,
  });
}

export async function confirmSisMatching(districtId: string, revision: number): Promise<SisDistrict> {
  return write('POST', `/admin/sis-matching/${encodeURIComponent(districtId)}/confirm`, { revision });
}
